import React, { useEffect, useRef, useState } from 'react'

export default function TeamStudentMapSection({ pins, colors, translations }) {
  const mapContainerRef = useRef(null)
  const mapRef = useRef(null)
  const [mapLoaded, setMapLoaded] = useState(false)
  const [mapError, setMapError] = useState(null)

  // Stable references to avoid re-initializing the map on every render
  const pinsRef = useRef(pins)
  const colorsRef = useRef(colors)
  pinsRef.current = pins
  colorsRef.current = colors

  useEffect(() => {
    if (!pinsRef.current || pinsRef.current.length === 0) return

    let map
    let isMounted = true

    const initMap = async () => {
      try {
        const L = (await import('leaflet')).default

        // Load Leaflet CSS from CDN (avoids esbuild PNG loader issues)
        if (!document.getElementById('leaflet-css')) {
          const link = document.createElement('link')
          link.id = 'leaflet-css'
          link.rel = 'stylesheet'
          link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'
          document.head.appendChild(link)
        }

        if (!isMounted || !mapContainerRef.current) return

        // Prevent duplicate map initialization
        if (mapRef.current) {
          mapRef.current.remove()
          mapRef.current = null
        }

        const currentPins = pinsRef.current
        const currentColors = colorsRef.current

        map = L.map(mapContainerRef.current, {
          center: [currentPins[0].latitude, currentPins[0].longitude],
          zoom: 3,
          attributionControl: true
        })

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
          maxZoom: 18
        }).addTo(map)

        mapRef.current = map

        // Add markers
        const markers = []

        currentPins.forEach((pin) => {
          const avatarHtml = pin.avatarUrl
            ? `<div style="width:40px;height:40px;border-radius:50%;border:3px solid ${currentColors.primary};background:white;background-image:url(${escapeAttr(pin.avatarUrl)});background-size:cover;background-position:center;box-shadow:0 2px 8px rgba(0,0,0,0.15);"></div>`
            : `<div style="width:40px;height:40px;border-radius:50%;border:3px solid ${currentColors.primary};background:white;display:flex;align-items:center;justify-content:center;font-size:16px;font-weight:bold;color:${currentColors.primary};box-shadow:0 2px 8px rgba(0,0,0,0.15);">${escapeHtml(pin.displayName.charAt(0))}</div>`

          const icon = L.divIcon({
            html: avatarHtml,
            className: 'student-map-marker-leaflet',
            iconSize: [46, 46],
            iconAnchor: [23, 23],
            popupAnchor: [0, -25]
          })

          const popupContent = `
            <div style="padding:4px 0;min-width:120px;">
              <div style="font-weight:600;font-size:14px;color:#222;">${escapeHtml(pin.displayName)}</div>
              ${pin.quote ? `<div style="font-size:13px;color:#666;margin-top:4px;line-height:1.4;">「${escapeHtml(pin.quote)}」</div>` : ''}
              <div style="font-size:12px;color:#999;margin-top:4px;">${escapeHtml(pin.cityName)}</div>
            </div>
          `

          const marker = L.marker([pin.latitude, pin.longitude], { icon })
            .bindPopup(popupContent, { closeButton: false, maxWidth: 220 })
            .addTo(map)

          markers.push(marker)
        })

        // Fit bounds
        if (currentPins.length === 1) {
          map.setView([currentPins[0].latitude, currentPins[0].longitude], 10)
        } else {
          const group = L.featureGroup(markers)
          map.fitBounds(group.getBounds(), { padding: [40, 40], maxZoom: 12 })
        }

        setMapLoaded(true)
      } catch (err) {
        console.error('Failed to load map:', err)
        if (isMounted) setMapError(true)
      }
    }

    initMap()

    return () => {
      isMounted = false
      if (map) map.remove()
      mapRef.current = null
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (!pins || pins.length === 0) return null

  return (
    <section className="mb-8 relative z-0">
      <style>{`.student-map-marker-leaflet { background: none !important; border: none !important; }`}</style>
      <h2 className="text-xl font-bold mb-4" style={{ color: colors.text.primary }}>
        🌍 {translations?.title || 'Student Locations'}
      </h2>
      {mapError ? (
        <div className="rounded-xl bg-gray-50 p-8 text-center text-gray-500">
          Failed to load map
        </div>
      ) : (
        <div className="relative rounded-xl overflow-hidden shadow-sm border border-gray-200">
          {!mapLoaded && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-10">
              <div className="animate-pulse text-gray-400">Loading map...</div>
            </div>
          )}
          <div
            ref={mapContainerRef}
            style={{ width: '100%', height: '400px' }}
          />
        </div>
      )}
    </section>
  )
}

function escapeHtml(str) {
  if (!str) return ''
  const div = document.createElement('div')
  div.textContent = str
  return div.innerHTML
}

function escapeAttr(str) {
  if (!str) return ''
  return str.replace(/"/g, '&quot;').replace(/'/g, '&#39;')
}
